/**
 * Google Drive Client
 *
 * This module provides functions to interact with Google Drive API.
 * All user data (notes, events, calendar) is stored in the user's Google Drive.
 *
 * Architecture:
 * - Uses OAuth token from Supabase session (via Google provider)
 * - Creates folder structure: NIDOnote/spaces/[space-id]/notes/
 * - Stores data as JSON files
 * - Includes retry logic with exponential backoff
 * - Rate limiting to prevent quota exhaustion
 */

import {
  retryWithBackoff,
  RetryableError,
  CircuitBreaker,
  driveRateLimiter,
} from './retry-utils';
import { getAccessToken, onTokenRefresh } from './firebase';

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  createdTime: string;
  modifiedTime: string;
}

interface DriveFolder {
  id: string;
  name: string;
}

class GoogleDriveClient {
  private accessToken: string | null = null;
  private readonly API_BASE = 'https://www.googleapis.com/drive/v3';
  private readonly UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3';
  private circuitBreaker = new CircuitBreaker(5, 60000); // 5 failures, 1 minute timeout

  constructor() {
    // Subscribe to token refresh events
    onTokenRefresh((newToken) => {
      if (newToken) {
        console.log('[GoogleDriveClient] Token refreshed, updating client');
        this.accessToken = newToken;
      } else {
        console.warn('[GoogleDriveClient] Token cleared');
        this.accessToken = null;
      }
    });
  }

  /**
   * Initialize the Drive client with an OAuth access token
   */
  initialize(accessToken: string) {
    this.accessToken = accessToken;
    console.log('[GoogleDriveClient] Client initialized with access token');
  }

  /**
   * Check if client is initialized
   */
  isInitialized(): boolean {
    return this.accessToken !== null;
  }

  /**
   * Ensure we have a valid token before making API calls
   * Automatically refreshes if token is expired
   */
  private async ensureValidToken(): Promise<string> {
    // Try to get a fresh token (this checks expiry and refreshes if needed)
    const freshToken = await getAccessToken();

    if (freshToken) {
      this.accessToken = freshToken;
      return freshToken;
    }

    // Fall back to current token if refresh fails
    if (this.accessToken) {
      return this.accessToken;
    }

    throw new Error('No valid access token available. Please sign in again.');
  }

  /**
   * Make an authenticated request to Google Drive API
   * Includes retry logic, rate limiting, and circuit breaker
   * Automatically ensures token is valid before making requests
   */
  private async request(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    // Ensure we have a valid, non-expired token
    const token = await this.ensureValidToken();

    return this.circuitBreaker.execute(async () => {
      return retryWithBackoff(
        async () => {
          // Apply rate limiting
          await driveRateLimiter.throttle();

          const response = await fetch(`${this.API_BASE}${endpoint}`, {
            ...options,
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              ...options.headers,
            },
          });

          if (!response.ok) {
            // Parse retry-after header if present
            const retryAfter = response.headers.get('Retry-After');
            let retryAfterSeconds: number | undefined;

            if (retryAfter) {
              const parsed = parseInt(retryAfter, 10);
              retryAfterSeconds = isNaN(parsed)
                ? Math.floor((new Date(retryAfter).getTime() - Date.now()) / 1000)
                : parsed;
            }

            const error = await response.json().catch(() => ({}));
            const errorMessage = `Drive API error: ${
              error.error?.message || response.statusText
            }`;

            throw new RetryableError(
              errorMessage,
              response.status,
              retryAfterSeconds
            );
          }

          return response;
        },
        {
          maxRetries: 5,
          baseDelayMs: 1000,
          maxDelayMs: 30000,
          jitter: true,
          retryableErrors: [429, 500, 502, 503, 504],
        }
      );
    });
  }

  /**
   * Create a folder in Google Drive
   */
  async createFolder(name: string, parentId?: string): Promise<DriveFolder> {
    const metadata = {
      name,
      mimeType: 'application/vnd.google-apps.folder',
      ...(parentId && { parents: [parentId] }),
    };

    const response = await this.request('/files', {
      method: 'POST',
      body: JSON.stringify(metadata),
    });

    const data = await response.json();
    return {
      id: data.id,
      name: data.name,
    };
  }

  /**
   * Find a folder by name and optional parent
   */
  async findFolder(name: string, parentId?: string): Promise<DriveFolder | null> {
    let query = `name='${name}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
    if (parentId) {
      query += ` and '${parentId}' in parents`;
    }

    const response = await this.request(
      `/files?q=${encodeURIComponent(query)}&fields=files(id,name)`
    );

    const data = await response.json();
    if (data.files && data.files.length > 0) {
      return {
        id: data.files[0].id,
        name: data.files[0].name,
      };
    }

    return null;
  }

  /**
   * Get or create a folder (find existing or create new)
   */
  async getOrCreateFolder(name: string, parentId?: string): Promise<DriveFolder> {
    const existing = await this.findFolder(name, parentId);
    if (existing) {
      return existing;
    }
    return this.createFolder(name, parentId);
  }

  /**
   * Upload a file to Google Drive
   */
  async uploadFile(
    fileName: string,
    content: string | object,
    folderId?: string,
    mimeType: string = 'application/json'
  ): Promise<DriveFile> {
    // Ensure we have a valid token
    const token = await this.ensureValidToken();

    const metadata = {
      name: fileName,
      mimeType,
      ...(folderId && { parents: [folderId] }),
    };

    const fileContent = typeof content === 'string' ? content : JSON.stringify(content);

    const form = new FormData();
    form.append(
      'metadata',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' })
    );
    form.append('file', new Blob([fileContent], { type: mimeType }));

    const response = await fetch(`${this.UPLOAD_BASE}/files?uploadType=multipart`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: form,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Upload failed: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();
    return {
      id: data.id,
      name: data.name,
      mimeType: data.mimeType,
      createdTime: data.createdTime,
      modifiedTime: data.modifiedTime,
    };
  }

  /**
   * Update an existing file in Google Drive
   */
  async updateFile(fileId: string, content: string | object): Promise<DriveFile> {
    // Ensure we have a valid token
    const token = await this.ensureValidToken();

    const fileContent = typeof content === 'string' ? content : JSON.stringify(content);

    const response = await fetch(`${this.UPLOAD_BASE}/files/${fileId}?uploadType=media`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: fileContent,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Update failed: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();
    return {
      id: data.id,
      name: data.name,
      mimeType: data.mimeType,
      createdTime: data.createdTime,
      modifiedTime: data.modifiedTime,
    };
  }

  /**
   * Download a file from Google Drive
   */
  async downloadFile(fileId: string): Promise<string> {
    const response = await this.request(`/files/${fileId}?alt=media`);
    return response.text();
  }

  /**
   * List files in a folder
   */
  async listFiles(folderId?: string, mimeType?: string): Promise<DriveFile[]> {
    let query = 'trashed=false';

    if (folderId) {
      query += ` and '${folderId}' in parents`;
    }

    if (mimeType) {
      query += ` and mimeType='${mimeType}'`;
    }

    const response = await this.request(
      `/files?q=${encodeURIComponent(query)}&fields=files(id,name,mimeType,createdTime,modifiedTime)&orderBy=modifiedTime desc`
    );

    const data = await response.json();
    return data.files || [];
  }

  /**
   * Delete a file from Google Drive
   */
  async deleteFile(fileId: string): Promise<void> {
    await this.request(`/files/${fileId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Find a file by name in a specific folder
   */
  async findFile(fileName: string, folderId?: string): Promise<DriveFile | null> {
    let query = `name='${fileName}' and trashed=false`;
    if (folderId) {
      query += ` and '${folderId}' in parents`;
    }

    const response = await this.request(
      `/files?q=${encodeURIComponent(query)}&fields=files(id,name,mimeType,createdTime,modifiedTime)`
    );

    const data = await response.json();
    if (data.files && data.files.length > 0) {
      return data.files[0];
    }

    return null;
  }

  /**
   * Initialize NIDOnote folder structure in Google Drive
   * Structure: NIDOnote/spaces/[space-id]/notes/
   */
  async initializeFolderStructure(): Promise<{
    rootFolder: DriveFolder;
    spacesFolder: DriveFolder;
  }> {
    // Create root NIDOnote folder
    const rootFolder = await this.getOrCreateFolder('NIDOnote');

    // Create spaces folder inside NIDOnote
    const spacesFolder = await this.getOrCreateFolder('spaces', rootFolder.id);

    return {
      rootFolder,
      spacesFolder,
    };
  }

  /**
   * Get or create a space folder structure
   */
  async getOrCreateSpaceFolder(spaceId: string): Promise<{
    spaceFolder: DriveFolder;
    notesFolder: DriveFolder;
    calendarFolder: DriveFolder;
    contentItemsFolder: DriveFolder;
  }> {
    const { spacesFolder } = await this.initializeFolderStructure();

    // Create space folder
    const spaceFolder = await this.getOrCreateFolder(spaceId, spacesFolder.id);

    // Create notes, calendar, and content-items folders inside space
    const notesFolder = await this.getOrCreateFolder('notes', spaceFolder.id);
    const calendarFolder = await this.getOrCreateFolder('calendar', spaceFolder.id);
    const contentItemsFolder = await this.getOrCreateFolder('content-items', spaceFolder.id);

    return {
      spaceFolder,
      notesFolder,
      calendarFolder,
      contentItemsFolder,
    };
  }
}

// Export singleton instance
export const driveClient = new GoogleDriveClient();

// Export types
export type { DriveFile, DriveFolder };
